import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdTrendingUp, MdGavel, MdHowToVote, MdArrowForward } from 'react-icons/md';

/**
 * Compact horizontal ticker that shows trending items (bills or polls) with
 * the most engagement in a recent window. Hidden when the API returns nothing.
 *
 * Variants:
 * - kind="bills": fetches /api/bills/me/trending, deep-links to /bills?focus=<key>
 * - kind="polls": fetches /api/polls/trending, deep-links to /polls?highlight=<id>
 */
export default function TrendingTicker({ kind = 'bills', hours = 24, limit = 2 }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url =
          kind === 'polls'
            ? `${API_URL}/api/polls/trending`
            : `${API_URL}/api/bills/me/trending`;
        const res = await axios.get(url, { params: { hours, limit } });
        if (cancelled) return;
        if (kind === 'polls') {
          setItems(res.data?.polls || []);
        } else {
          setItems(res.data?.bills || []);
        }
      } catch (e) {
        // Silently degrade — no ticker is fine.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [kind, hours, limit]);

  if (!loaded || items.length === 0) return null;

  const isPolls = kind === 'polls';
  const labelMetric = (n, suffix) => `${n} ${n === 1 ? suffix : suffix + 's'}`;
  const headline = isPolls ? 'Trending polls today' : 'Trending votes today';
  const Icon = isPolls ? MdHowToVote : MdGavel;
  const cta = isPolls ? 'Open' : 'View';

  return (
    <div
      className="rounded-card border border-amber-200 bg-gradient-to-r from-amber-50 via-white to-amber-50 px-4 py-3 mb-4 shadow-sm"
      data-testid={`trending-ticker-${kind}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-white shrink-0">
          <MdTrendingUp className="text-base" />
        </span>
        <span className="text-xs font-rubik font-semibold text-amber-800 uppercase tracking-wide">
          {headline}
        </span>
        <span className="text-xs text-amber-700 hidden sm:inline">
          · last {hours}h
        </span>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        {items.map((it, idx) => {
          const onClick = () => {
            if (isPolls) {
              navigate(`/polls/${encodeURIComponent(it.pollId)}`);
            } else {
              navigate(`/bills?focus=${encodeURIComponent(it.bill_key)}`);
            }
          };
          const recent = isPolls ? it.recent_responses : it.recent_votes;
          const title = isPolls ? it.question : (it.bill_name || it.bill_id);
          const recentSuffix = isPolls ? 'response' : 'vote';
          return (
            <button
              key={idx}
              type="button"
              onClick={onClick}
              className="flex-1 min-w-0 flex items-start gap-3 p-3 rounded-card bg-white/80 hover:bg-white border border-amber-100 hover:border-amber-300 transition-all text-left group"
              data-testid={`trending-ticker-item-${kind}-${idx}`}
            >
              <div className="text-amber-600 shrink-0 mt-0.5">
                <Icon className="text-xl" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 font-rubik line-clamp-2 group-hover:text-primary transition-colors">
                  {title}
                </p>
                <p className="mt-0.5 text-xs text-amber-700 font-rubik font-semibold tabular-nums">
                  +{labelMetric(recent, recentSuffix)} in the last {hours}h
                </p>
              </div>
              <span className="hidden md:inline-flex items-center gap-1 text-xs font-rubik text-amber-700 shrink-0 self-center">
                {cta} <MdArrowForward className="text-base" />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
